import { Outlet, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, SparklesIcon, TruckIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { ROUTES } from '@/utils/constants';

const AuthLayout = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex">
      {/* Left Side - Branding & Information */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-primary-600 via-primary-500 to-accent-500 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          {/* Logo & Title */}
          <Link to={ROUTES.HOME} className="flex items-center gap-3 group">
            <div className="w-16 h-16 relative flex items-center justify-center transition-transform group-hover:scale-110">
              <svg
                viewBox="0 0 64 64"
                className="w-full h-full drop-shadow-2xl"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <filter id="glow-auth">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>

                {/* Bin Body */}
                <rect x="14" y="24" width="36" height="32" rx="2" fill="#2d4a52" stroke="#fff" strokeWidth="1.5"/>

                {/* Vertical Stripes with Glow */}
                <rect x="18" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" filter="url(#glow-auth)" opacity="0.9"/>
                <rect x="24" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" filter="url(#glow-auth)" opacity="0.9"/>
                <rect x="30" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" filter="url(#glow-auth)" opacity="0.9"/>
                <rect x="36" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" filter="url(#glow-auth)" opacity="0.9"/>
                <rect x="42" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" filter="url(#glow-auth)" opacity="0.9"/>

                {/* Bin Lid */}
                <rect x="10" y="18" width="44" height="8" rx="2" fill="#2d4a52" stroke="#fff" strokeWidth="1.5" transform="rotate(-8 32 22)"/>

                {/* Lid Opening */}
                <ellipse cx="32" cy="22" rx="18" ry="3" fill="#60a5fa" filter="url(#glow-auth)" opacity="0.7"/>

                {/* Sparkles */}
                <g filter="url(#glow-auth)">
                  <path d="M 40 8 L 41 11 L 44 10 L 41 12 L 42 15 L 40 13 L 38 15 L 39 12 L 36 10 L 39 11 Z" fill="#60a5fa" opacity="0.9"/>
                  <path d="M 48 4 L 48.5 5.5 L 50 5 L 48.5 6 L 49 7.5 L 48 6.5 L 47 7.5 L 47.5 6 L 46 5 L 47.5 5.5 Z" fill="#60a5fa" opacity="0.9"/>
                </g>

                {/* Dots */}
                <circle cx="45" cy="6" r="1.5" fill="#60a5fa" filter="url(#glow-auth)" opacity="0.8"/>
                <circle cx="50" cy="8" r="1" fill="#60a5fa" filter="url(#glow-auth)" opacity="0.8"/>
                <circle cx="43" cy="10" r="1" fill="#60a5fa" filter="url(#glow-auth)" opacity="0.8"/>

                {/* Base/Shadow */}
                <ellipse cx="32" cy="58" rx="20" ry="2" fill="#1e2d33" opacity="0.6"/>
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">RecyclerBin</h1>
              <p className="text-sm text-white/80">Your sustainable marketplace</p>
            </div>
          </Link>

          {/* Main Content */}
          <div className="space-y-12">
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-4xl lg:text-5xl font-bold leading-tight mb-6"
              >
                Shop Smart, Live Sustainably
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-lg text-white/90 leading-relaxed max-w-md"
              >
                Join thousands of eco-conscious shoppers buying and selling quality products
                while making a positive impact on our planet.
              </motion.p>
            </div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-4"
            >
              {[
                { icon: ShieldCheckIcon, text: 'Secure & trusted platform' },
                { icon: SparklesIcon, text: 'Premium quality products' },
                { icon: TruckIcon, text: 'Fast & reliable delivery' },
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-5 w-5" />
                  </div>
                  <span className="text-lg">{feature.text}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Footer */}
          <div className="text-sm text-white/70">
            © {new Date().getFullYear()} RecyclerBin. All rights reserved.
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-40 h-40 bg-accent-400/20 rounded-full blur-3xl"></div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to={ROUTES.HOME} className="inline-flex items-center gap-3">
              <div className="w-12 h-12 relative">
                <svg
                  viewBox="0 0 64 64"
                  className="w-full h-full"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="14" y="24" width="36" height="32" rx="2" fill="#2d4a52" stroke="#1e2d33" strokeWidth="1.5"/>
                  <rect x="18" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" opacity="0.9"/>
                  <rect x="24" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" opacity="0.9"/>
                  <rect x="30" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" opacity="0.9"/>
                  <rect x="36" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" opacity="0.9"/>
                  <rect x="42" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" opacity="0.9"/>
                  <rect x="10" y="18" width="44" height="8" rx="2" fill="#2d4a52" stroke="#1e2d33" strokeWidth="1.5" transform="rotate(-8 32 22)"/>
                  <ellipse cx="32" cy="22" rx="18" ry="3" fill="#60a5fa" opacity="0.7"/>
                  <ellipse cx="32" cy="58" rx="20" ry="2" fill="#1e2d33" opacity="0.6"/>
                </svg>
              </div>
              <div className="text-left">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">RecyclerBin</h1>
              </div>
            </Link>
          </div>

          {/* Form Card */}
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-200 dark:border-dark-700 p-8">
            <Outlet />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthLayout;
